import { Box, Stack, useTheme } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import { isMaxNumberOfUsersReached } from 'utils/VisitorGroup/VisitorGroup';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useAllVisitors from 'v2/hooks/useAllVisitors/useAllVisitors';
import AddUserToGroupDrawer from 'v2/pages/Users/Groups/Details/components/AddUserToGroupDrawer';
import EmptyUsersTab from 'v2/pages/Users/Groups/Details/components/EmptyUsersTab';
import AllUsersOverview from 'v2/pages/Users/Overview/components/AllUsersOverview';
import { useUsersSearchAndFilters } from 'v2/pages/Users/Overview/useUsersSearchAndFilters';

interface Props {
  visitorGroup: VisitorGroup;
  isAddVisitorButtonVisible: boolean;
}

const UsersTab: React.FC<Props> = ({ visitorGroup, isAddVisitorButtonVisible }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const { openDrawer } = useDrawerContext();

  const groupAutocompleteOption = { label: visitorGroup.name, value: visitorGroup.id };

  const handleOpenAddUserDrawer = () => {
    openDrawer(
      <AddUserToGroupDrawer
        preSelectedGroup={groupAutocompleteOption}
        hideGroupField
        isMaxNumberOfUsersReached={isMaxNumberOfUsersReached(visitorGroup)}
      />
    );
  };

  const isMaxNumberOfUsersReachedCheck = isMaxNumberOfUsersReached(visitorGroup);

  const { allVisitors, allVisitorsCards, loadingAllVisitors, loadingAllVisitorsCards } = useAllVisitors({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const membersOfSelectedVisitorGroups = useMemo(
    () =>
      allVisitors.filter((visitor) =>
        visitor.visitorGroupMembership.some((membership) => membership.visitorGroup.id === visitorGroup.id)
      ),
    [allVisitors, visitorGroup.id]
  );

  const { filteredVisitors, filterStr, setFilterStr, sections, chips, countCaption } = useUsersSearchAndFilters({
    allVisitors: membersOfSelectedVisitorGroups,
    allVisitorsCards
  });

  const isLoading = loadingAllVisitors || loadingAllVisitorsCards;

  if (!isLoading && membersOfSelectedVisitorGroups.length === 0) {
    return (
      <EmptyUsersTab
        visitorGroup={visitorGroup}
        isAddVisitorButtonVisible={isAddVisitorButtonVisible && !isMaxNumberOfUsersReachedCheck}
      />
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        borderRadius: 1,
        width: '100%'
      }}
    >
      <Box sx={{ backgroundColor: theme.custom.background.level2 }}>
        <SearchAndFilters
          filterStr={filterStr}
          onFilterStrChange={setFilterStr}
          sections={sections}
          chips={chips}
          countCaption={countCaption}
          actionButton={
            isAddVisitorButtonVisible
              ? {
                  id: 'add-user-button',
                  label: translation.add_user,
                  onClick: handleOpenAddUserDrawer,
                  disabled: isMaxNumberOfUsersReachedCheck,
                  tooltip: isMaxNumberOfUsersReachedCheck ? translation.maximum_amount_of_users_reached : undefined
                }
              : undefined
          }
        />
      </Box>
      <Box sx={{ backgroundColor: theme.custom.background.level3, px: 3, pb: 3 }}>
        <AllUsersOverview
          visitors={filteredVisitors}
          cards={allVisitorsCards}
          variant="Tab"
          loading={isLoading}
          className="background-level2"
          groupId={visitorGroup.id}
        />
      </Box>
    </Stack>
  );
};

export default UsersTab;
